import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

export type TipoEvento = 'visita' | 'cita' | 'tarea';

export const NOMBRE_TIPO: Record<TipoEvento, string> = {
  visita: 'Visita',
  cita: 'Cita',
  tarea: 'Tarea',
};

/** Un evento de la Agenda. `fecha` AAAA-MM-DD y `hora` HH:MM (opcional). */
export interface Evento {
  event_id: string;
  tipo: TipoEvento;
  titulo: string;
  fecha: string;
  hora?: string;
  property_id?: string;
  client_id?: string;
  notas?: string;
  hecho: boolean;
}

/** Los eventos de varios meses (AAAA-MM) de una vez: la vista pide el que
 *  enseña, el anterior y el siguiente. */
export function useEventos(meses: string[]) {
  return useQuery<Evento[]>({
    queryKey: ['eventos', ...meses],
    queryFn: async () =>
      (await api.get('/crm/events', { params: { meses: meses.join(',') } })).data.items,
  });
}

export function useGuardarEvento() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (e: Partial<Evento>) => {
      const { event_id, ...body } = e;
      return (event_id
        ? await api.put(`/crm/events/${event_id}`, body)
        : await api.post('/crm/events', body)
      ).data as Evento;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['eventos'] }),
  });
}

export function useBorrarEvento() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await api.delete(`/crm/events/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['eventos'] }),
  });
}

export interface Atencion {
  solicitudes: { lead_id: string; nombre: string; que: string; dias: number }[];
  contratos: { contract_id: string; property_id: string; vivienda: string; inquilino: string; fecha_fin: string; dias: number }[];
  incidencias: { incident_id: string; vivienda: string; categoria: string; dias: number }[];
}

export function useAtencion() {
  return useQuery<Atencion>({
    queryKey: ['agenda-atencion'],
    queryFn: async () => (await api.get('/crm/agenda/atencion')).data,
  });
}

export interface Avisos {
  /** false mientras SES siga en sandbox; null si no se pudo saber. */
  ses_produccion: boolean | null;
  activo: boolean;
  destinatarios: string[];
  ultimo_envio?: string;
  ultimo_resultado?: string;
  ultimo_ok?: boolean;
}

/** Solo admin: la configuración del resumen diario por correo. */
export function useAvisos(activo: boolean) {
  return useQuery<Avisos>({
    queryKey: ['agenda-avisos'],
    queryFn: async () => (await api.get('/crm/agenda/avisos')).data,
    enabled: activo,
  });
}

export function useGuardarAvisos() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (a: Pick<Avisos, 'activo' | 'destinatarios'>) =>
      (await api.put('/crm/agenda/avisos', a)).data as Avisos,
    onSuccess: (data) => qc.setQueryData(['agenda-avisos'], data),
  });
}

/** Manda ahora el resumen a los destinatarios guardados. El resultado queda en
 *  la configuración, así que después se vuelve a leer. */
export function useProbarAvisos() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => (await api.post('/crm/agenda/avisos/probar', null, { timeout: 60000 })).data,
    onSettled: () => qc.invalidateQueries({ queryKey: ['agenda-avisos'] }),
  });
}
